import React, { useEffect, useMemo, useState } from "react";
import { X, ChevronLeft, ChevronRight, Sparkles, Layers, ShieldCheck, Send, PenTool } from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";

const TOUR_KEY = "wi-pa-tour-seen-v1";

// The five stages mirror what THIS app actually builds. Stage 6 (welcome) is
// just the entry card. Time estimates below are calibrated against real
// in-home visit observation: an experienced RN runs ~12 min total, a new
// RN ~25 min — keeping the headline "30 → 12" promise.
const STEPS = [
  {
    icon: null, // welcome step uses the AvidaLogo instead
    eyebrow: "Welcome",
    title: "Welcome to your intake assistant",
    body: "A tablet-first wizard that turns a 30-minute home visit into a clean, signature-ready PA packet in roughly 12 minutes. Five quick stages. Strict compliance baked in.",
    metaLabel: "Total time",
    meta: "≈ 12 min experienced · 25 min new RN",
    cta: "Show me the stages",
  },
  {
    icon: Layers,
    eyebrow: "Stage 1",
    title: "Capture the visit",
    body: "Steps 1–7 cover client demographics, ADLs (with one-tap quick-set patterns), MAHC-10 fall risk, Braden, NSA narratives, medications, diagnoses, vitals, and the cert period. Use the blue Dictate button on every narrative — Whisper turns 60 seconds of speech into clean text.",
    metaLabel: "Time",
    meta: "≈ 7 min in the home",
    features: ["Voice dictation", "Photo → med list", "Photo → insurance card", "Saved agency profile"],
    cta: "Next: Verify",
  },
  {
    icon: ShieldCheck,
    eyebrow: "Stage 2",
    title: "Verify before you sign",
    body: "Step 8 runs Claude Sonnet 4.5 over the full payload checking all compliance rules in real time. Step 9 walks you through a 13-item RN checklist that gates Generate. Errors are caught now, not at the payer.",
    metaLabel: "Time",
    meta: "≈ 1 min review",
    features: ["AI compliance check", "13-item RN checklist", "Inline error jump-to-step"],
    cta: "Next: Generate",
  },
  {
    icon: Sparkles,
    eyebrow: "Stage 3",
    title: "Generate the PA packet",
    body: "Eight PDFs stamped pixel-perfect onto the official state templates: F-11133, F-11134, F-11136, CMS-485, NSA, Plan of Care, Care Plan, plus a merged Submission Packet. Built from your findings — zero retyping.",
    metaLabel: "Time",
    meta: "≈ 30 sec to render",
    features: ["8 PDFs", "Exact template overlays", "Audit receipt PDF included"],
    cta: "Next: Sign",
  },
  {
    icon: PenTool,
    eyebrow: "Stage 4",
    title: "Finger-drawn dual signatures",
    body: "Two signatures with dates: the RN and the client (or POA). Drawn directly on the tablet, backed up to IndexedDB so a refresh never loses them, and embedded into every PDF as a proper PNG. No typed names accepted.",
    metaLabel: "Time",
    meta: "≈ 1 min for both",
    features: ["Biometric signature pad", "IndexedDB backup", "PNG-embedded in PDFs"],
    cta: "Next: Handoff",
  },
  {
    icon: Send,
    eyebrow: "Stage 5",
    title: "Hand off to the next system",
    body: "When you tap Generate, the bundle (8 PDFs + signatures + DHS audit + visit metadata) ships via webhook to the parent app, which owns long-term storage, payer fax, and approval tracking. You see live delivery status on the Download Center.",
    metaLabel: "Time",
    meta: "Delivered in seconds",
    features: ["Webhook handoff", "Audit receipt for your records", "Live delivery status"],
    cta: "Got it — start a visit",
  },
];

export default function TourOverlay() {
  const [visible, setVisible] = useState(false);
  const [i, setI] = useState(0);

  useEffect(() => {
    // Only auto-show on the landing page. Admins who deep-link to
    // /settings/brand, RNs who resume into /wizard/N, or anyone else
    // hitting an interior route already know the app — they don't need
    // the welcome onboarding. The "Tour" replay button in the wizard
    // header still works for explicit re-runs.
    try {
      if (window.location.pathname !== "/") return;
      if (!localStorage.getItem(TOUR_KEY)) setVisible(true);
    } catch {
      /* localStorage unavailable — non-fatal */
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(TOUR_KEY, new Date().toISOString()); } catch { /* localStorage unavailable — tour will replay */ }
    setVisible(false);
  };

  const step = useMemo(() => STEPS[i], [i]);
  if (!visible) return null;
  const last = i === STEPS.length - 1;
  const Icon = step.icon;
  const isWelcome = i === 0;

  return (
    <div className="fixed inset-0 z-[100] bg-[#0B1E3A]/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="tour-overlay">
      <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-[0_24px_64px_rgba(26,33,28,0.3)] border border-[#D6E3EF] overflow-hidden">
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-3 top-3 text-[#3A5275] hover:text-[#0B1E3A] p-1.5 rounded-full hover:bg-[#E7F1FB] z-10"
          aria-label="Close tour"
          data-testid="tour-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="bg-gradient-to-br from-[#E7F1FB] via-[#f4f6f1] to-[#FFFDEC] px-8 pt-10 pb-6 text-center">
          {isWelcome ? (
            <div className="flex justify-center mb-5">
              <AvidaLogo size="lg" testid="tour-avida-logo" />
            </div>
          ) : (
            <div className="inline-flex w-16 h-16 rounded-2xl bg-[#0A3D79] text-[#F7FAFC] items-center justify-center mb-4 shadow-md">
              {Icon ? <Icon className="w-7 h-7" strokeWidth={1.7} /> : null}
            </div>
          )}
          {step.eyebrow && (
            <div className="eyebrow-blue text-[10.5px] mb-2" data-testid={`tour-eyebrow-${i}`}>{step.eyebrow}</div>
          )}
          <h3 className="font-display text-2xl font-semibold text-[#0B1E3A] mb-2 tracking-tight">
            {step.title}
          </h3>
          <p className="text-[14px] text-[#1E2E4A] leading-relaxed max-w-md mx-auto">{step.body}</p>

          {step.meta && (
            <div className="mt-4 inline-flex items-center gap-2 px-3 h-7 rounded-full bg-white border border-[#D6E3EF]">
              <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#8A6B00]">{step.metaLabel || "Time"}</span>
              <span className="text-[12px] font-semibold text-[#0B1E3A]">{step.meta}</span>
            </div>
          )}

          {step.features?.length ? (
            <div className="mt-4 flex flex-wrap justify-center gap-1.5 max-w-md mx-auto">
              {step.features.map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center px-2.5 h-6 rounded-full bg-white border border-[#D6E3EF] text-[11px] font-semibold text-[#0A3D79]"
                  data-testid={`tour-feature-${i}-${f.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {f}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex gap-1.5" aria-label="tour progress">
            {STEPS.map((s, idx) => (
              <span
                key={s.title}
                className={`w-2 h-2 rounded-full transition ${idx === i ? "bg-[#0A3D79] w-5" : idx < i ? "bg-[#1FA0E6]" : "bg-[#D6E3EF]"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button
                type="button"
                onClick={() => setI(i - 1)}
                className="h-9 px-3 rounded-lg border border-[#D6E3EF] text-[13px] font-semibold text-[#0B1E3A] inline-flex items-center gap-1 hover:bg-[#f4f6f1]"
                data-testid="tour-back"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            {!last ? (
              <button
                type="button"
                onClick={() => setI(i + 1)}
                className="btn-primary !h-9"
                data-testid="tour-next"
              >
                {step.cta} <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={dismiss}
                className="btn-primary !h-9"
                data-testid="tour-finish"
              >
                {step.cta} <Sparkles className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
